import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { SCORECARD_COMMON_GOAL_TYPE_REGULAR } from 'constants/scorecards';
import { parseParamTypes } from 'helpers';
import { formTexts } from 'i18n';
import {
  selectDefaultAssessmentScaleGroupId,
  selectOptions,
} from 'store/options';
import { FormGoal } from 'components/GoalsAndSubgoalsForm/interfaces';

/**
 * Returns a factory that produces the default shape for a newly-added goal
 * — including one default subgoal — matching the OLD `useCreateNewGoal`
 * behaviour from `frontend/src/hooks/GoalsAndSubgoalsForm/useCreateNewGoal.ts`.
 *
 * The boolean flags (`allowScorecardOwnerEdits`, `allowScorecardComment`,
 * `selfAssessmentSetup`, etc.) are seeded from the server-driven
 * `paramTypes[i].defaultValue` via `parseParamTypes`. EditScorecard always
 * edits a scorecard (never a template), so `isTemplate` is false here.
 */
export default function useNewGoalDefaults() {
  const { formatMessage } = useIntl();
  const defaultAssessmentScaleGroupId = useSelector(
    selectDefaultAssessmentScaleGroupId,
  );
  const { paramTypes } = useSelector(selectOptions);

  return useCallback(
    (orderRank: number): FormGoal => {
      const {
        allowScorecardComment,
        allowScorecardOwnerEdits,
        allowScorecardMetricPmOwnerEdits,
        allowScorecardOwnerEditActualField,
        autoAssignPmReviewer,
        limitReviewersToEmployeeAndPm,
        selfAssessmentSetup,
      } = parseParamTypes(paramTypes, false);

      return {
        name: formatMessage(formTexts.untitledGoal),
        description: '',
        weight: 0,
        type: SCORECARD_COMMON_GOAL_TYPE_REGULAR,
        orderRank,
        subgoals: [
          {
            name: formatMessage(formTexts.untitledSubgoal),
            description: '',
            weight: 100,
            orderRank: 0,
            assessmentScaleGroupId: defaultAssessmentScaleGroupId,
          },
        ],
        isCustom: true,
        allowScorecardComment,
        allowScorecardOwnerEdits,
        allowScorecardMetricPmOwnerEdits,
        allowScorecardOwnerEditActualField,
        autoAssignPmReviewer,
        limitReviewersToEmployeeAndPm,
        selfAssessmentSetup,
      } as unknown as FormGoal;
    },
    [defaultAssessmentScaleGroupId, formatMessage, paramTypes],
  );
}
